/**
 * The Convert dialog — `lead-detail.html`'s three-column Account / Contact /
 * Opportunity layout, ported.
 *
 * `POST /leads/{id}/convert` answers 501 (`leads.routes.js`'s
 * `LEAD_VERBS_NOT_BUILT`), so this always runs through `useConvertLead`'s mock
 * fallback today — but the body it sends is shaped exactly like `docs/API.md`
 * §4's documented contract, so nothing here has to change the day the endpoint
 * ships.
 *
 * "Choose Existing" is disabled on both Account and Contact: it needs a
 * type-ahead search against `GET /accounts` / `GET /contacts` that this dialog
 * does not wire up, and a select with one fake option is worse than a
 * disabled one that says why. The prototype ships the same asymmetry — its own
 * Contact picker is a disabled placeholder too.
 */
import { useEffect, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { useCreateActivity } from '../activities/useActivities.js';

const DURATIONS = [15, 30, 45, 60, 90, 120];

/** `datetime-local` wants local `YYYY-MM-DDTHH:mm`, not UTC — tomorrow 10:00 is a sensible default slot. */
function defaultStart() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const isUrl = (v) => { try { return Boolean(new URL(v)); } catch { return false; } };

/**
 * Book a meeting with a lead — creates a real `meeting` activity linked to it
 * (`POST /activities`, `lead_id`), so it shows on the lead's Activity tab, the
 * calendar, and the rep's "today" lists.
 */
export function BookMeetingModal({ open, lead, onClose }) {
  const create = useCreateActivity();
  const [subject, setSubject] = useState('');
  const [start, setStart] = useState('');
  const [duration, setDuration] = useState(30);
  const [where, setWhere] = useState('');
  const [link, setLink] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setSubject(`Meeting with ${lead?.name ?? 'lead'}`);
    setStart(defaultStart());
    setDuration(30);
    setWhere('');
    setLink('');
    setNotes('');
  }, [open, lead?.name]);

  const startDate = start ? new Date(start) : null;
  const urlOk = !link.trim() || isUrl(link.trim());
  const canSave = Boolean(subject.trim() && startDate && !Number.isNaN(startDate.getTime()) && urlOk);

  const save = async () => {
    if (!canSave) return;
    const payload = {
      kind: 'meeting',
      subject: subject.trim(),
      lead_id: lead.id,
      scheduled_start: startDate.toISOString(),
      scheduled_end: new Date(startDate.getTime() + duration * 60_000).toISOString(),
      duration_minutes: duration,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (where.trim()) payload.location = where.trim();
    if (link.trim()) payload.meeting_url = link.trim();
    if (notes.trim()) payload.body = notes.trim();
    await create.mutateAsync(payload);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Book Meeting"
      size="lg"
      footer={(
        <>
          <span className="t-xs ink-3 me-auto">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            Saved as a meeting on this lead's activity timeline and your calendar.
          </span>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-save btn-sm-x" onClick={save} disabled={!canSave || create.isPending}>
            {create.isPending ? <><ButtonSpinner />Booking…</> : 'Book meeting'}
          </button>
        </>
      )}
    >
      <div className="t-xs ink-3 mb-3">{lead?.name} · {lead?.lead_no}</div>

      <div className="eyebrow mb-2">Meeting</div>
      <div className="row g-3 mb-3">
        <div className="col-12">
          <Field label="Subject" required>
            <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus />
          </Field>
        </div>
      </div>

      <div className="eyebrow mb-2">When &amp; Where</div>
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <Field label="Date &amp; time" required>
            <input type="datetime-local" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} />
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Duration">
            <select className="form-select" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              {DURATIONS.map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Location">
            <input className="form-control" value={where} onChange={(e) => setWhere(e.target.value)} placeholder="Office, client site…" />
          </Field>
        </div>
        <div className="col-12">
          <Field label="Meeting link" error={urlOk ? undefined : "That doesn't look like a valid link."}>
            <input className="form-control" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://meet.google.com/…" />
          </Field>
        </div>
      </div>

      <div className="eyebrow mb-2">Agenda</div>
      <Field>
        <textarea className="form-control" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Topics to cover, who should attend…" />
      </Field>
    </Modal>
  );
}

/** The contact designations a company's buying group is made of — saved on the contact's job title. */
export const DESIGNATIONS = [
  'CEO', 'Director', 'Purchase Manager', 'IT Manager', 'Finance Manager', 'Decision Maker', 'Influencer',
];

/**
 * @param {{matches:Array<{id:string,account_no:string,name:string}>}|null} conflict
 *   Set by the page when the API answered `account_name_conflict`: the dialog then
 *   offers to use the existing account instead of silently making a twin.
 */
export function ConvertModal({ open, lead, options, busy, conflict, onClose, onConfirm }) {
  const [oppSkip, setOppSkip] = useState(false);
  const [oppName, setOppName] = useState('');
  const [oppAmount, setOppAmount] = useState('');
  const [oppClose, setOppClose] = useState('');
  const [contactName, setContactName] = useState('');
  const [designation, setDesignation] = useState('');
  const [accountName, setAccountName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [moveActivities, setMoveActivities] = useState(true);

  useEffect(() => {
    if (!open || !lead) return;
    setOppSkip(false);
    setOppName(`${lead.company} — ${lead.industry && lead.industry !== '—' ? lead.industry : 'New business'}`);
    setOppAmount(lead.value ?? '');
    setOppClose('');
    setContactName(lead.name ?? '');
    setDesignation(lead.title && lead.title !== '—' ? lead.title : '');
    setAccountName(lead.company ?? '');
    setOwnerId('');
    setStatusId('');
    setMoveActivities(true);
  }, [open, lead]);

  if (!lead) return null;

  /* The lead's own title is kept as an option when it is not one of the standard
     designations, so converting does not silently overwrite it. */
  const designationOptions = designation && !DESIGNATIONS.includes(designation)
    ? [designation, ...DESIGNATIONS] : DESIGNATIONS;

  const submit = (accountOverride) => {
    onConfirm({
      account: accountOverride ?? { mode: 'create', name: accountName },
      contact: { mode: 'create', is_primary: true, full_name: contactName, ...(designation ? { job_title: designation } : {}) },
      opportunity: oppSkip
        ? { mode: 'skip' }
        : { mode: 'create', name: oppName, amount: oppAmount === '' ? undefined : Number(oppAmount), currency: lead.currency, expected_close_date: oppClose || undefined },
      owner_id: ownerId || undefined,
      converted_status_id: statusId || undefined,
      move_activities: moveActivities,
      row_version: lead.raw?.row_version,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Convert Lead"
      size="lg"
      footer={
        <>
          <span className="t-xs ink-3 me-auto">
            <i className="bi bi-info-circle me-1" aria-hidden="true" />
            Conversion history is retained and the lead becomes read-only.
          </span>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-add btn-sm-x" disabled={busy} onClick={() => submit()}>
            {busy ? <><ButtonSpinner />Converting…</> : 'Convert'}
          </button>
        </>
      }
    >
      <div className="t-xs ink-3 mb-3">{lead.name} · {lead.lead_no}</div>

      {conflict?.length > 0 && (
        <div className="alert alert-warning t-sm mb-3" role="alert">
          <div className="fw-650 mb-1">
            <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
            An account named &ldquo;{accountName}&rdquo; already exists
          </div>
          <div className="mb-2">
            {conflict.map((m) => `${m.name} (${m.account_no})`).join(', ')}. Add this contact to it, or create a separate account anyway.
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              type="button" className="btn btn-add btn-sm-x" disabled={busy}
              onClick={() => submit({ mode: 'existing', id: conflict[0].id })}
            >
              Use existing account
            </button>
            <button
              type="button" className="btn btn-soft btn-sm-x" disabled={busy}
              onClick={() => submit({ mode: 'create', name: accountName, allow_duplicate: true })}
            >
              Create a new account anyway
            </button>
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-cool)' }}><i className="bi bi-building" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Account</div><div className="t-2xs ink-3">Company record</div></div>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="acct" id="acctExisting" disabled />
              <label className="form-check-label ink-4" htmlFor="acctExisting" title="Account search is not wired up in this build">Choose Existing</label>
            </div>
            <select className="form-select mb-2" disabled><option>Search accounts…</option></select>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="acct" id="acctNew" checked readOnly />
              <label className="form-check-label" htmlFor="acctNew">Create New</label>
            </div>
            <input className="form-control" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          </div>
        </div>

        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-mint)' }}><i className="bi bi-person" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Contact</div><div className="t-2xs ink-3">Person record</div></div>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="cont" id="contNew" checked readOnly />
              <label className="form-check-label" htmlFor="contNew">Create New</label>
            </div>
            <input className="form-control mb-2" value={contactName} onChange={(e) => setContactName(e.target.value)} aria-label="Contact name" />
            <label className="lbl" htmlFor="convDesignation">Designation</label>
            <select id="convDesignation" className="form-select mb-2" value={designation} onChange={(e) => setDesignation(e.target.value)}>
              <option value="">Select designation…</option>
              {designationOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="cont" id="contExisting" disabled />
              <label className="form-check-label ink-4" htmlFor="contExisting" title="Contact search is not wired up in this build">Choose Existing</label>
            </div>
            <select className="form-select" disabled><option>Search contacts…</option></select>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="convert-col">
            <div className="cc-head">
              <div className="cc-ico" style={{ background: 'var(--grad-brand)' }}><i className="bi bi-briefcase" aria-hidden="true" /></div>
              <div><div className="t-sm fw-700">Opportunity</div><div className="t-2xs ink-3">Deal record</div></div>
            </div>
            <input className="form-control mb-2" disabled={oppSkip} value={oppName} onChange={(e) => setOppName(e.target.value)} />
            <div className="row g-2">
              <div className="col-6">
                <label className="lbl">Amount</label>
                <input className="form-control" disabled={oppSkip} value={oppAmount} onChange={(e) => setOppAmount(e.target.value)} />
              </div>
              <div className="col-6">
                <label className="lbl">Close Date</label>
                <input className="form-control" type="date" disabled={oppSkip} value={oppClose} onChange={(e) => setOppClose(e.target.value)} />
              </div>
            </div>
            <div className="form-check mt-2">
              <input className="form-check-input" type="checkbox" id="oppSkip" checked={oppSkip} onChange={(e) => setOppSkip(e.target.checked)} />
              <label className="form-check-label" htmlFor="oppSkip">Don&rsquo;t create an opportunity</label>
            </div>
          </div>
        </div>
      </div>

      <hr className="soft" />
      <div className="row g-3 align-items-end">
        <div className="col-md-4">
          <Field label="Record Owner">
            <select className="form-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
              <option value="">Leave as {lead.owner ?? 'unassigned'}</option>
              {options.owners.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <Field label="Converted Status">
            <select className="form-select" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              <option value="">Tenant default</option>
              {options.statuses.filter((o) => /convert/i.test(o.label)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="col-md-4">
          <div className="form-check">
            <input className="form-check-input" type="checkbox" id="convActivities" checked={moveActivities} onChange={(e) => setMoveActivities(e.target.checked)} />
            <label className="form-check-label" htmlFor="convActivities">Carry over activities</label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
